import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface PageLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to include a sidebar
   * @default false
   */
  hasSidebar?: boolean;

  /**
   * Sidebar element
   */
  sidebar?: ReactNode;

  /**
   * Header element
   */
  header?: ReactNode;
}

/**
 * PageLayout component
 *
 * Main page layout component that composes header, sidebar, and content.
 *
 * @example
 * ```tsx
 * <PageLayout
 *   header={<Header>...</Header>}
 *   sidebar={<Sidebar>...</Sidebar>}
 *   hasSidebar
 * >
 *   <PageContent>
 *     Main page content
 *   </PageContent>
 * </PageLayout>
 * ```
 */
export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  ({ hasSidebar = false, sidebar, header, className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('min-h-screen flex flex-col', className)} {...props}>
        {header}
        <div className="flex flex-1 overflow-hidden">
          {hasSidebar && sidebar}
          <main className="flex-1 overflow-y-auto">{children}</main>
        </div>
      </div>
    );
  }
);

PageLayout.displayName = 'PageLayout';

export interface PageContentProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Maximum width constraint
   * @default 'full'
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

  /**
   * Whether to center the content
   * @default false
   */
  centered?: boolean;
}

/**
 * PageContent component
 *
 * Content container with optional max-width constraints.
 */
export const PageContent = forwardRef<HTMLDivElement, PageContentProps>(
  ({ maxWidth = 'full', centered = false, className, children, ...props }, ref) => {
    const maxWidthStyles = {
      sm: 'max-w-screen-sm',
      md: 'max-w-screen-md',
      lg: 'max-w-screen-lg',
      xl: 'max-w-screen-xl',
      '2xl': 'max-w-screen-2xl',
      full: 'max-w-full',
    };

    return (
      <div
        ref={ref}
        className={cn('p-6', maxWidthStyles[maxWidth], centered && 'mx-auto', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

PageContent.displayName = 'PageContent';

export interface PageHeaderProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * PageHeader component
 *
 * Page header section with title and actions.
 */
export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('mb-6 flex items-center justify-between', className)} {...props}>
        {children}
      </div>
    );
  }
);

PageHeader.displayName = 'PageHeader';

export interface PageTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /**
   * HTML heading level
   * @default 'h1'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * Subtitle text
   */
  subtitle?: string;
}

/**
 * PageTitle component
 *
 * Page title with optional subtitle.
 */
export const PageTitle = forwardRef<HTMLHeadingElement, PageTitleProps>(
  ({ as: Component = 'h1', subtitle, className, children, ...props }, ref) => {
    return (
      <div>
        <Component
          ref={ref}
          className={cn('text-2xl font-bold text-gray-900', className)}
          {...props}
        >
          {children}
        </Component>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
    );
  }
);

PageTitle.displayName = 'PageTitle';
